import React, { useState } from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  arrow,
  useClick,
} from '@floating-ui/react'
import { TooltipProps } from '../types'
import { TooltipArrow } from './TooltipArrow'

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  placement = 'top',
  delay = 200,
  showArrow = true,
  className = '',
  disabled = false,
  maxWidth = 'max-w-xs',
  trigger = ['hover', 'focus'],
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = React.useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen && !disabled,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(showArrow ? 12 : 8), // 如果有箭头，增加距离
      flip({
        fallbackAxisSideDirection: 'start',
        padding: 8,
      }),
      shift({ padding: 8 }),
      ...(showArrow ? [arrow({ element: arrowRef })] : []),
    ],
  })

  // 根据 trigger 配置启用不同的交互方式
  const hover = useHover(context, {
    enabled: trigger.includes('hover'),
    delay: { open: delay, close: 150 },
  })
  const focus = useFocus(context, {
    enabled: trigger.includes('focus'),
  })
  const click = useClick(context, {
    enabled: trigger.includes('click'),
  })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ])

  if (disabled) {
    return <>{children}</>
  }

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()} className="cursor-help">
        {children}
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            style={{ ...floatingStyles, zIndex: 101 }}
            className={`
              z-[101] px-3 py-2 cursor-help
              bg-fill-page-secondary
              text-text-primary text-sm font-medium
              rounded-lg shadow-lg
              relative
              ${maxWidth}
              ${className}
            `}
          >
            {content}
            {showArrow && (
              <TooltipArrow
                ref={arrowRef}
                placement={context.placement || placement}
              />
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
